import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage, PageGuideResource } from '@/types/resources';
import { api } from '@/utils/api';
import { download } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';

const Dialog = styled(StyledDialog)`
  width: 640px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const StatusText = styled(Text)`
  margin: 0 0 16px;
`;

const ErrorText = styled(Text)`
  margin: 0 0 16px;
  color: ${({ theme }) => theme.colors.error};
`;

const Preview = styled.pre`
  margin: 0;
  padding: 16px;
  max-height: 40vh;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  ${scrollBarStyles};
`;

const Spinner = styled(ContentSpinner)`
  margin: 40px 0;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 32px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

const getErrorStatus = (error: unknown) =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const PageGuideModal: FC<Props> = ({ activePage, onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const queryKey = ['page_guide', activePage.externalId];

  const {
    data: guide,
    isLoading,
    error,
  } = useQuery(
    queryKey,
    () =>
      api.get<never, PageGuideResource>(
        `/v1/data/page-guides/${activePage.externalId}`,
      ),
    {
      retry: (failureCount, err) => {
        const status = getErrorStatus(err);

        return status !== 404 && status !== 403 && failureCount < 2;
      },
      refetchInterval: (data) => (data?.status === 'pending' ? 5_000 : false),
    },
  );

  const guideNotFound = getErrorStatus(error) === 404;
  const guideNotSupported = getErrorStatus(error) === 403;

  const triggerGeneration = async (force = false) => {
    try {
      setIsGenerating(true);

      const guide = await api.post<never, PageGuideResource>(
        `/v1/data/page-guides/${activePage.externalId}`,
        { force },
      );

      queryClient.setQueryData(queryKey, guide);
    } catch (_) {
      alerts.error();
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!guide?.markdown) {
      return;
    }

    const fileName = `${activePage.name.toLowerCase().replace(/\s+/g, '-')}-page-guide.md`;

    download(guide.markdown, fileName, 'text/markdown', false);
  };

  const renderContent = () => {
    if (isLoading) {
      return <Spinner />;
    }

    if (guideNotSupported) {
      return (
        <StatusText $type="body" $color="gray">
          Page guides are only available for Facebook business pages.
        </StatusText>
      );
    }

    if (guideNotFound) {
      return (
        <StatusText $type="body" $color="gray">
          No page guide has been generated for {activePage.name} yet.
        </StatusText>
      );
    }

    if (!guide) {
      return (
        <ErrorText $type="body">
          Failed to load the page guide. Please try again later.
        </ErrorText>
      );
    }

    if (guide.status === 'pending') {
      return (
        <>
          <StatusText $type="body" $color="gray">
            Guide generation is in progress. This can take a few minutes — the
            status will update automatically.
          </StatusText>
          <Spinner />
        </>
      );
    }

    if (guide.status === 'failed') {
      return (
        <>
          <StatusText $type="body" $color="gray">
            The last generation attempt failed:
          </StatusText>
          <ErrorText $type="body">{guide.error ?? 'Unknown error'}</ErrorText>
        </>
      );
    }

    return (
      <>
        <StatusText $type="body" $color="gray">
          Generated
          {guide.generated_at &&
            ` on ${DateTime.fromISO(guide.generated_at).toFormat('yyyy/LL/dd HH:mm')}`}
        </StatusText>
        {guide.markdown && <Preview>{guide.markdown}</Preview>}
      </>
    );
  };

  const renderActionButton = () => {
    if (isLoading || guideNotSupported) {
      return null;
    }

    if (guideNotFound || guide?.status === 'failed') {
      return (
        <FooterButton
          type="button"
          disabled={isGenerating}
          onClick={() => triggerGeneration()}
        >
          {guideNotFound ? 'Generate guide' : 'Retry generation'}
        </FooterButton>
      );
    }

    if (guide?.status === 'ready') {
      return (
        <>
          <FooterButton
            type="button"
            color="secondary"
            disabled={isGenerating}
            onClick={() => triggerGeneration(true)}
          >
            Regenerate
          </FooterButton>
          <FooterButton type="button" onClick={handleDownload}>
            Download
          </FooterButton>
        </>
      );
    }

    return null;
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Page Guide · {activePage.name}</Heading>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        {renderContent()}
        <Footer>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Close
          </FooterButton>
          {renderActionButton()}
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default PageGuideModal;
